import React from 'react';
import {
  Dialog,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  Button,
  IconButton,
  useTheme,
  useMediaQuery,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Divider,
  Chip,
} from '@mui/material';
import {
  Close as CloseIcon,
  Flag as FlagIcon,
  ReportProblem as ReportProblemIcon,
  Shield as ShieldIcon,
} from '@mui/icons-material';
import { motion } from 'framer-motion';
import { createPortal } from 'react-dom';
import type { ReportReason } from '../../services/reportService';

interface ReportProductDialogProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (payload: { subject: string; reason: ReportReason; message: string }) => Promise<void> | void;
  productTitle?: string;
  sellerName?: string;
}

const REASONS: Array<{ value: ReportReason; label: string; helper: string }> = [
  {
    value: 'prohibited_item',
    label: 'Prohibited item',
    helper: 'Illegal or restricted goods.',
  },
  {
    value: 'counterfeit',
    label: 'Counterfeit / replica',
    helper: 'Suspected fake brand or counterfeit item.',
  },
  {
    value: 'misleading_description',
    label: 'Misleading description',
    helper: 'Photos/details don’t match the listing.',
  },
  {
    value: 'fraud_scam',
    label: 'Fraud / scam',
    helper: 'Suspicious listing or scam behavior.',
  },
  {
    value: 'stolen_item',
    label: 'Stolen item',
    helper: 'Suspected stolen goods.',
  },
  {
    value: 'hate_harassment',
    label: 'Hate / harassment',
    helper: 'Abusive content or hate speech.',
  },
  {
    value: 'adult_content',
    label: 'Adult content',
    helper: 'Explicit or adult content.',
  },
  {
    value: 'other',
    label: 'Other',
    helper: 'Anything else not listed above.',
  },
];

const ReportProductDialog: React.FC<ReportProductDialogProps> = ({
  open,
  onClose,
  onSubmit,
  productTitle,
  sellerName,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const [reason, setReason] = React.useState<ReportReason | ''>('');
  const [subject, setSubject] = React.useState('Report listing');
  const [message, setMessage] = React.useState('');
  const [submitting, setSubmitting] = React.useState(false);
  const [touched, setTouched] = React.useState(false);

  React.useEffect(() => {
    if (!open) return;
    setReason('');
    setSubject('Report listing');
    setMessage('');
    setTouched(false);
    setSubmitting(false);
  }, [open]);

  const selectedReason = React.useMemo(() => {
    return REASONS.find((r) => r.value === reason) || null;
  }, [reason]);

  const subjectError = touched && !subject.trim();
  const reasonError = touched && !reason;
  const messageError = touched && message.trim().length < 10;

  const handleSubmit = async () => {
    setTouched(true);
    if (!subject.trim() || !reason || message.trim().length < 10) return;

    try {
      setSubmitting(true);
      await onSubmit({
        subject: subject.trim(),
        reason: reason as ReportReason,
        message: message.trim(),
      });
      onClose();
    } finally {
      setSubmitting(false);
    }
  };

  if (!open) return null;

  const content = (
    <Dialog
      open={open}
      onClose={submitting ? undefined : onClose}
      maxWidth="sm"
      fullWidth
      fullScreen={isMobile}
      PaperProps={{
        sx: {
          borderRadius: isMobile ? 0 : 2.5,
          overflow: 'hidden',
          bgcolor: 'background.paper',
          border: '1px solid rgba(0,0,0,0.12)',
          boxShadow: '0 12px 40px rgba(0,0,0,0.22)',
        },
      }}
      BackdropProps={{ sx: { bgcolor: 'rgba(0, 0, 0, 0.55)' } }}
    >
      <DialogContent sx={{ p: 0 }}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1.25,
            px: { xs: 2.5, sm: 3 },
            py: 2,
            borderBottom: '1px solid',
            borderColor: 'divider',
            bgcolor: 'background.paper',
          }}
        >
          <Box
            sx={{
              width: 36,
              height: 36,
              borderRadius: 2,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              bgcolor: 'rgba(239,68,68,0.10)',
              border: '1px solid rgba(239,68,68,0.20)',
              flexShrink: 0,
            }}
          >
            <FlagIcon sx={{ color: 'error.main' }} />
          </Box>
          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Typography sx={{ fontSize: 18, fontWeight: 800, lineHeight: 1.2 }}>
              Report this listing
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Tell us what’s wrong so we can review it.
            </Typography>
          </Box>
          <IconButton onClick={onClose} disabled={submitting} aria-label="Close">
            <CloseIcon />
          </IconButton>
        </Box>

        <Box sx={{ p: { xs: 2.5, sm: 3 } }}>
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.25 }}
          >
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
              <Chip
                icon={<ShieldIcon />}
                label="Reports are reviewed"
                size="small"
                sx={{ fontWeight: 700 }}
              />
              <Chip
                icon={<ReportProblemIcon />}
                label="Don’t share sensitive info"
                size="small"
                sx={{ fontWeight: 700 }}
              />
            </Box>

            {(productTitle || sellerName) && (
              <Box
                sx={{
                  p: 2,
                  borderRadius: 2,
                  border: '1px solid',
                  borderColor: 'divider',
                  bgcolor: 'grey.50',
                  mb: 2,
                }}
              >
                {productTitle && (
                  <Typography sx={{ fontWeight: 800, mb: 0.25 }} noWrap>
                    {productTitle}
                  </Typography>
                )}
                {sellerName && (
                  <Typography variant="body2" color="text.secondary">
                    Seller: {sellerName}
                  </Typography>
                )}
              </Box>
            )}

            <Divider sx={{ mb: 2 }} />

            <Box sx={{ display: 'grid', gap: 2 }}>
              <TextField
                label="Subject"
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
                onBlur={() => setTouched(true)}
                error={subjectError}
                helperText={subjectError ? 'Please enter a subject.' : 'A short summary (e.g., “Counterfeit item”).'}
                fullWidth
              />

              <FormControl fullWidth error={reasonError}>
                <InputLabel id="report-reason-label">Reason</InputLabel>
                <Select
                  labelId="report-reason-label"
                  value={reason}
                  label="Reason"
                  onChange={(e) => setReason(e.target.value as any)}
                  onClose={() => setTouched(true)}
                >
                  {REASONS.map((r) => (
                    <MenuItem key={r.value} value={r.value}>
                      <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                        <Typography sx={{ fontWeight: 700 }}>{r.label}</Typography>
                        <Typography variant="caption" color="text.secondary">
                          {r.helper}
                        </Typography>
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
                <Typography variant="caption" color={reasonError ? 'error.main' : 'text.secondary'} sx={{ mt: 0.75 }}>
                  {reasonError
                    ? 'Please select a reason.'
                    : selectedReason
                      ? selectedReason.helper
                      : 'Choose the option that best matches the issue.'}
                </Typography>
              </FormControl>

              <TextField
                label="Details"
                placeholder="Tell us what’s wrong with this listing…"
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                onBlur={() => setTouched(true)}
                error={messageError}
                helperText={messageError ? 'Please provide at least 10 characters.' : 'Include links, messages, or context that helps us investigate.'}
                multiline
                minRows={4}
                fullWidth
              />
            </Box>
          </motion.div>
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: { xs: 2.5, sm: 3 }, pb: { xs: 2.5, sm: 3 }, pt: 0 }}>
        <Box sx={{ width: '100%', display: 'flex', gap: 1.25, flexDirection: { xs: 'column-reverse', sm: 'row' } }}>
          <Button
            onClick={onClose}
            disabled={submitting}
            variant="outlined"
            size="large"
            sx={{
              flex: 1,
              borderRadius: 2,
              textTransform: 'none',
              fontWeight: 700,
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={submitting}
            variant="contained"
            size="large"
            sx={{
              flex: 1,
              borderRadius: 2,
              textTransform: 'none',
              fontWeight: 800,
            }}
          >
            {submitting ? 'Submitting…' : 'Submit report'}
          </Button>
        </Box>
      </DialogActions>
    </Dialog>
  );

  return createPortal(content, document.body);
};

export default ReportProductDialog;
